import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { PRIMARY_OUTLET, Router, UrlSegment, UrlTree } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { ProductComponentId, ProductConfigurationIds } from '../../models/cart-model';
import { Chapter, PagePath } from '../../models/page-model';
import { TEALIUM_TAGS } from '../constants.service';
import { ExperimentsService } from '../experiments/experiments.service';
import { QuestionnaireService } from '../questionnaire/questionnaire.service';
import { WindowRef } from '../windowRef.service';
import { AppService } from '../../state/app';

@Injectable()
export class TrackingService {
  constructor(
    public winRef: WindowRef,
    private cookieService: CookieService,
    private questionnaireService: QuestionnaireService,
    private router: Router,
    private appService: AppService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.tealiumCookieMap[TEALIUM_TAGS.GA_PROD] = 'C0002';
    this.tealiumCookieMap[TEALIUM_TAGS.GA_QA] = 'C0002';
    this.tealiumCookieMap[TEALIUM_TAGS.AudienceStream] = 'C0003';
    this.tealiumCookieMap[TEALIUM_TAGS.AttributionJS_QA] = 'C0002';
    this.tealiumCookieMap[TEALIUM_TAGS.AttributionJS_DEV] = 'C0002';
    this.tealiumCookieMap[TEALIUM_TAGS.AttributionJS_PROD] = 'C0002';
    this.tealiumCookieMap[TEALIUM_TAGS.Kenshoo_AllNonZeroDollarConversion] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Adwords_Conversion_Retargeting] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.BingAds_OrderConfirmEvent] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Facebook_Order_Complete_Events] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Facebook_Order_Complete_Events_2] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Conversion_trackTransaction] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Confirm_Image_Pixels] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.PepperJam_TealiumPixel] = 'C0002';
    this.tealiumCookieMap[TEALIUM_TAGS.Zeta_Conversion_Tracking] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Zeta_Page_Tracking] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Facebook_Start_Events] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.Proactive_Chat] = 'C0003';
    this.tealiumCookieMap[TEALIUM_TAGS.Criteo_View_Cart] = 'C0004';
    this.tealiumCookieMap[TEALIUM_TAGS.LLC_Inc_Checkout_Tracking] = 'C0004';
  }

  private tealiumCookieMap = {};

  private prefix = 'js_page.oCMOrder.';
  private firstTrack = true;

  private gaProductIDs = [
    { iP: '1', sDesc: 'Inc' },
    { iP: '2', sDesc: 'LLC' },
    { iP: '3', sDesc: 'LT' },
    { iP: '4', sDesc: 'Div' },
    { iP: '6', sDesc: 'LWT' },
    { iP: '7', sDesc: 'TM' },
    { iP: '8', sDesc: 'Copyright' },
    { iP: '9', sDesc: 'LW' },
    { iP: '11', sDesc: 'POA' },
    { iP: '13', sDesc: 'TMSrch' },
    { iP: '14', sDesc: 'TMMon' },
    { iP: '15', sDesc: 'NameChg' },
    { iP: '18', sDesc: 'DBA' },
    { iP: '20', sDesc: 'NP' },
    { iP: '22', sDesc: 'SMClaims' },
    { iP: '23', sDesc: 'LP' },
    { iP: '24', sDesc: 'LLP' },
    { iP: '25', sDesc: 'Amend' },
    { iP: '26', sDesc: 'Dissolution' },
    { iP: '27', sDesc: 'FQ' },
    { iP: '29', sDesc: 'CertGdStnd' },
    { iP: '35', sDesc: 'SMClaims' },
    { iP: '38', sDesc: 'ResLease' },
    { iP: '39', sDesc: 'CorpMinPrep' },
    { iP: '40', sDesc: 'LWT' },
    { iP: '42', sDesc: 'CorpMinSub' },
    { iP: '44', sDesc: 'SellPermit' },
    { iP: '45', sDesc: 'TaxID' },
    { iP: '47', sDesc: 'Conversion' },
    { iP: '48', sDesc: 'REDeed' },
    { iP: '49', sDesc: 'EINobt' },
    { iP: '51', sDesc: 'TMSect8' },
    { iP: '52', sDesc: 'TMRenew' },
    { iP: '53', sDesc: 'TMUse' },
    { iP: '54', sDesc: 'TMUseExt' },
    { iP: '55', sDesc: 'TMAssign' },
    { iP: '57', sDesc: 'BusLic' },
    { iP: '59', sDesc: '501c3' },
    { iP: '60', sDesc: 'Bkrpt' },
    { iP: '62', sDesc: 'Forms' },
    { iP: '63', sDesc: 'GPAgmt' },
    { iP: '64', sDesc: 'JtVenture' },
    { iP: '65', sDesc: 'PatSrch' },
    { iP: '67', sDesc: 'PatProv' },
    { iP: '68', sDesc: 'ENC' },
    { iP: '69', sDesc: 'PatDesign' },
    { iP: '70', sDesc: 'PatUtil' },
    { iP: '71', sDesc: 'PatFiling' },
    { iP: '72', sDesc: 'AttyPrenup' },
    { iP: '73', sDesc: 'ContRev' },
    { iP: '75', sDesc: 'BylawRes' },
    { iP: '76', sDesc: 'OpAgmt' },
    { iP: '77', sDesc: 'CompanyCerts' },
    { iP: '78', sDesc: 'CompanySeal' },
    { iP: '79', sDesc: 'CorpKit' },
    { iP: '80', sDesc: 'InitRep' },
    { iP: '81', sDesc: 'AnnRep' },
    { iP: '82', sDesc: 'CertCopies' },
    { iP: '83', sDesc: 'RA' },
    { iP: '84', sDesc: 'PatDraw' },
    { iP: '85', sDesc: 'SepAgmt' },
    { iP: '86', sDesc: 'DemLtr' },
    { iP: '87', sDesc: 'ContDraft' },
    { iP: '88', sDesc: 'EmplAgmt' },
    { iP: '89', sDesc: 'ChSuppMod' },
    { iP: '90', sDesc: 'ChCustMod' },
    { iP: '91', sDesc: 'SpsSuppMod' },
    { iP: '92', sDesc: 'FrncAgmtRev' },
    { iP: '93', sDesc: 'Scorp' },
    { iP: '94', sDesc: 'FormsSub' },
    { iP: '95', sDesc: 'LegAdvPlusPlan' },
    { iP: '96', sDesc: 'StepAdoption' },
    { iP: '97', sDesc: 'TMDispLtr' },
    { iP: '98', sDesc: 'HomeSale' },
    { iP: '99', sDesc: 'EPVault' },
    { iP: '100', sDesc: 'PetProtAgmt' },
    { iP: '101', sDesc: 'Guardianship' },
    { iP: '102', sDesc: 'AttyDiv' },
    { iP: '103', sDesc: 'PrenupRev' },
    { iP: '105', sDesc: 'LegalSep' },
    { iP: '106', sDesc: 'Postnup' },
    { iP: '107', sDesc: 'ENR' },
    { iP: '108', sDesc: 'CompCal' },
    { iP: '109', sDesc: 'BizAdvSA' },
    { iP: '112', sDesc: 'LegAdvSA' },
    { iP: '122', sDesc: 'BusAdvAttyConsultation' },
    { iP: '123', sDesc: 'BusAdvIPAttyConsultation' },
    { iP: '124', sDesc: 'LegalAdvAttyConsultation' },
    { iP: '126', sDesc: 'LWT' },
    { iP: '127', sDesc: 'POA' },
    { iP: '128', sDesc: 'LW' },
    { iP: '133', sDesc: 'AttyPrenup' },
    { iP: '147', sDesc: 'SamsClubBundle' },
    { iP: '636', sDesc: 'PrenupRev' },
    { iP: '149', sDesc: 'EpBundle' },
    { iP: '150', sDesc: 'altm' },
    { iP: '-99', sDesc: 'UNK' }
  ];

  private LLCPageFlowMap = [
    {
      name: PagePath.BusinessState,
      trackingName: 'state_selection'
    },
    {
      name: PagePath.ChangeName,
      trackingName: 'search_different_name'
    },
    {
      name: PagePath.NameAvailable,
      trackingName: 'name_available'
    },
    {
      name: PagePath.BusinessStartTime,
      trackingName: 'launch_date'
    },
    {
      name: PagePath.BusinessStartTimeDetail,
      trackingName: 'launch_date_congrats'
    },
    {
      name: PagePath.FirstLlc,
      trackingName: 'first_corp'
    },
    {
      name: PagePath.BusinessPurpose,
      trackingName: 'industry'
    },
    // {
    //   name: PagePath.BusinessPurposeDetail,
    //   trackingName: 'industry_congrats'
    // },
    {
      name: PagePath.HireEmployees,
      trackingName: 'employees_in_first_year'
    },
    {
      name: Chapter.Essentials,
      trackingName: 'essential_services_intro'
    },
    {
      name: PagePath.RegisteredAgent,
      trackingName: 'registered_agent'
    },
    {
      name: PagePath.GSuitePackage,
      trackingName: 'gsuite'
    },
    {
      name: PagePath.CriticalDocuments,
      trackingName: 'essential_docs'
    },
    {
      name: PagePath.StateTax,
      trackingName: 'state_tax_registration'
    },
    {
      name: Chapter.Services,
      trackingName: 'included_services_intro'
    },
    {
      name: PagePath.Tax,
      trackingName: 'tax'
    },
    {
      name: PagePath.Legal,
      trackingName: 'legal'
    },
    {
      name: PagePath.Compliance,
      trackingName: 'compliance'
    },
    {
      name: PagePath.PackageSelection,
      trackingName: 'package_selection'
    },
    {
      name: PagePath.BofA,
      trackingName: 'BofA_checking'
    },
    {
      name: PagePath.ReviewYourOrder,
      trackingName: 'rya'
    },
    {
      name: PagePath.Checkout,
      trackingName: 'ccinfo'
    },
    {
      name: PagePath.ExpressOffersPage,
      trackingName: 'lwt_tm'
    },
    {
      name: PagePath.OrderConfirmation,
      trackingName: 'Orderdone'
    },
    {
      name: PagePath.RemainingQuestions,
      trackingName: 'ques'
    },
    {
      name: PagePath.PartnerOffers,
      trackingName: 'partner_offers'
    },
    {
      name: PagePath.OrderConfirmationRibbon,
      trackingName: 'congratulations'
    },
    {
      name: PagePath.OrderConfirmationCongrats,
      trackingName: 'social_share'
    },
  ];

  // maybe we move this to a separate model class
  // UDOs that need to  be set by client and definied in Tealium Data Layer must be passed here
  public trackingModel = {
    'js_page.oCMOrder.iProcess': '',
    'js_page.oCMOrder.iQuestionnaire': '',
    'js_page.oCMOrder.oTestCheck.sCheckValue': '',
    'js_page.oCMOrder.partner': '',
    'js_page.oCMOrder.sOrderAmount': '',
    'js_page.oCMOrder.sPaymentPlan': '',
    'js_page.oCMOrder.sPage': '',
    'js_page.oCMOrder.sProcess': '',
    'js_page.oCMOrder.sReopen': '',
    'js_page.oCMOrder.sUserOrderID': '',
    'js_page.oCMOrder.oaItems': [],
    'js_page.oCMOrder.oDiscount.sAmount': '',
    'js_page.oCMOrder.oDiscount.sCode': '',
    'js_page.oCMOrder.oUser.sCustomerID': '',
    'js_page.oCMOrder.oUser.sCustomerState': '',
    'js_page.oCMOrder.oUser.sCustomerZip': '',
    'js_page.oCMOrder.oUser.sEmail': '',
    'js_page.oCMOrder.oUser.sFName': '',
    'js_page.oCMOrder.oUser.sLName': '',
    'js_page.oCMOrder.isAgent': '',
    'js_page.oCMOrder.submitToGA': null,
    // GA data
    uga_pagename: '',
    'js_page.ga_parent_total': 0,
    'js_page.ga_sub_total': 0,
    'js_page.lzt_customerid': '',
    'js_page.lzt_discountamount': '',
    'js_page.lzt_orderid': '',
    'js_page.lzt_ordertotal': '',
    'js_page.lzt_promocode': '',
    'js_page.lzt_purchasetype': '',
    // GA arrays
    'js_page.ga_arrays.sAmount': [],
    'js_page.ga_arrays.sProductID': [],
    'js_page.ga_arrays.sQuantity': [],
    'js_page.ga_arrays.gaCat': [],
    'js_page.ga_arrays.sku': [],
    'js_page.ga_arrays.sRelationshipType': [],
    'js_page.ga_arrays.sSalesTax': [],
    'js_page.ga_arrays.sProductName': [],
    // GA Store for differentiating orders from legacy
    'js_page.ga_store_flag': '',
    // checkout page tracking pixel stuff
    product_name: '',
    product_price: '',
    criteo_site_type_val: '',
    email: '',
  };

  private filterUnapprovedTags(tags: number[]): number[] {
    const nativeWin = this.winRef.nativeWindow;
    const approvedCookieCats: string[] = nativeWin.OptanonActiveGroups ? nativeWin.OptanonActiveGroups.split(',') : ['C0001'];
    return tags.filter(tag => approvedCookieCats.indexOf(this.tealiumCookieMap[tag]) >= 0);
  }

  public TriggerTracking(trackType: string = ''): void {
    const nativeWin = this.winRef.nativeWindow;
    let tagIDs = [
      TEALIUM_TAGS.AttributionJS_QA,
      TEALIUM_TAGS.AttributionJS_PROD,
      TEALIUM_TAGS.GA_PROD,
      TEALIUM_TAGS.GA_QA,
      TEALIUM_TAGS.AudienceStream,
    ];

    switch (trackType) {
      case PagePath.ExpressOffersPage: {
        tagIDs = tagIDs.concat([
          TEALIUM_TAGS.Kenshoo_AllNonZeroDollarConversion,
          TEALIUM_TAGS.BingAds_OrderConfirmEvent,
          TEALIUM_TAGS.Facebook_Order_Complete_Events,
          TEALIUM_TAGS.Facebook_Order_Complete_Events_2,
          TEALIUM_TAGS.Conversion_trackTransaction,
          TEALIUM_TAGS.Confirm_Image_Pixels,
          TEALIUM_TAGS.PepperJam_TealiumPixel,
          TEALIUM_TAGS.Zeta_Conversion_Tracking,
          TEALIUM_TAGS.Zeta_Page_Tracking,
        ]);
        break;
      }

      case 'checkoutPage':
        tagIDs = tagIDs.concat([TEALIUM_TAGS.LLC_Inc_Checkout_Tracking, TEALIUM_TAGS.Criteo_View_Cart]);
        break;

      default: {
        break;
      }
    }

    const tags = this.firstTrack ? null : this.filterUnapprovedTags(tagIDs);
    this.firstTrack = false;

    if (nativeWin.utag !== undefined) {
      // since utag data merges don't apply for limited tags, perform it ahead of time
      nativeWin.utag.ut.merge(nativeWin.utag.data, this.trackingModel, 1);
      if (tags && tags.length) {
        nativeWin.utag.handler.RE('view', nativeWin.utag.data, 'blr');
      }
      nativeWin.utag.view(nativeWin.utag.data, null, tags);
      // this.tealiumService.link(this.trackingModel);
    } else {
      // TODO: shortcut for now will use a promise
      setTimeout(() => {
        nativeWin.utag.view(this.trackingModel, null, tags);
        // this.tealiumService.link(this.trackingModel);
      }, 1000);
    }
  }

  private getTrackingNameFromUrl(urlTree: UrlTree): string {
    const productName = 'llc';
    const productType = 'biz';
    const environmentIdentifier = this.questionnaireService.llc.isMobile ? '_native_app' : '';

    const urlSegments: UrlSegment[] = urlTree.root.children[PRIMARY_OUTLET].segments;
    const sectionName = urlSegments[0].path === Chapter.PostOrder ? 'Q2' : 'Q1';
    const urlPageName = urlSegments[urlSegments.length - 1].path;

    const pageToTrack = this.LLCPageFlowMap.find(p => p.name === urlPageName);
    if (pageToTrack) {
      return `${productType}${environmentIdentifier}_${sectionName}_${pageToTrack.trackingName}-${productName}`;
    } else {
      return null;
    }
  }

  public SetCurrentPageToTrack(pageUrl: string): void {
    const utagModel = this.trackingModel;
    const nativeWin = this.winRef.nativeWindow;
    const urlTree: UrlTree = this.router.parseUrl(pageUrl);

    if (urlTree.root.numberOfChildren === 0) {
      return;
    }

    let currentPage = this.getTrackingNameFromUrl(urlTree);
    if (!currentPage) {
      currentPage = pageUrl;
    }
    // GA data
    utagModel.uga_pagename = currentPage;
    utagModel[`${this.prefix}sPage`] = currentPage;
    // for SEAD
    nativeWin.oCMOrder.sPage = currentPage;
    // Hari added on 11/06/2019 for B-47944
    if (this.cookieService.get('LOGGEDIN_AS_AGENT') === '1') {
      nativeWin.oCMOrder.isAgent = '1';
    }
    this.TriggerTracking();
  }

  public setCheckoutTrackingVariables({ revenue, pkgName }) {
    const email: string = this.appService.loginEmail || '';
    const utagModel = this.trackingModel;
    utagModel.product_name = pkgName;
    utagModel.product_price = revenue;
    utagModel.email = email;
    utagModel.criteo_site_type_val = this.questionnaireService.llc.isMobile ? 'm' : 'd';
    this.TriggerTracking();
  }

  public UpdateTrackingModel(): void {
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;

    if (this.appService.app != null) {
      utagModel[`${this.prefix}oUser.sCustomerID`] = this.appService.app.customerId != null ? this.appService.app.customerId.toString() : '';
      // for SEAD || Attribution
      nativeWin.oCMOrder.oUser.sCustomerID = this.appService.app.customerId != null ? this.appService.app.customerId.toString() : '';
    }

    if (this.appService.app != null) {
      // set up for the utagModel we pass back with utag.view()
      utagModel[`${this.prefix}iProcess`] = this.appService.app.processId != null ? this.appService.app.processId.toString() : '';
      utagModel[`${this.prefix}iQuestionnaire`] = this.appService.app.questionnaireId != null ? this.appService.app.questionnaireId.toString() : '';
      utagModel[`${this.prefix}oTestCheck.sCheckValue`] = this.appService.app.loginEmail != null ? this.appService.app.loginEmail : '';
      /*
      #############################################################
      # model.userOrder.UserOrderId is not used in GA analytics per Swapnil - 3/16/17
      # naming convention issue, oCMOrder.sUserOrderID does not represent the UserOrderId in Tealium
      # oCMOrder.sUserOrderID actually represents the paid transaction order ID
      # in this new system this is represented by the OrderId in the response object from checkoutService.checkout()
      */

      // utagModel[`${this.prefix}sUserOrderID`] = model.userOrder.UserOrderId != null ? model.userOrder.UserOrderId : '';
      // set up for the local object for SEAD
      nativeWin.oCMOrder.iProcess = this.appService.app.processId != null ? this.appService.app.processId.toString() : '';
      nativeWin.oCMOrder.iQuestionnaire = this.appService.app.questionnaireId != null ? this.appService.app.questionnaireId.toString() : '';
      nativeWin.oCMOrder.oTestCheck.sCheckValue = this.appService.app.loginEmail != null ? this.appService.app.loginEmail.toString() : '';
      // nativeWin.oCMOrder.sUserOrderID = model.userOrder.UserOrderId != null ? model.userOrder.UserOrderId : '';
      // nativeWin.lzt_orderid = model.userOrder.UserOrderId != null ? model.userOrder.UserOrderId : '';
    }
    this.TriggerTracking();
  }

  public ClearOAItems(): void {
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;

    utagModel[`${this.prefix}oaItems`] = [];
    nativeWin.oCMOrder.oaItems = [];
  }

  public MapToOAItems(dataModel: any): void {
    const model = dataModel;
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;
    // TODO: move this eventually to an enum
    const relationshipTypes = {
      0: 'UnSet',
      1: 'Process',
      2: 'Package',
      3: 'AddOn',
      4: 'IncludedItem',
      5: 'Upsell',
      6: 'Bundle',
      7: 'Shipping',
      8: 'FilingFee',
      9: 'CrossSell',
      10: 'Revision',
      11: 'Upgrade',
      12: 'Renewal',
      13: 'SubscriptionUsage',
    };

    if (model.cartItems.length > 0) {
      model.cartItems.forEach((element) => {
        if(relationshipTypes[element.type] === relationshipTypes[0]){
          return;
        }
        // const itemObj: OAItem = this.oaItem;
        // const itemObj = this.oaItem;
        const itemObj = {
          sProductID: '',
          sProductName: '',
          sAmount: '0',
          sQuantity: '1',
          sRelationshipType: null,
          sSalesTax: '0.00',
          sPaymentReceiver: '1'
        };

        itemObj.sProductID = element.productConfigurationId;
        itemObj.sProductName = element.title;
        itemObj.sAmount = element.amount === 0 ? '0.00' : element.amount;
        if (element.title.includes('No thanks')) {
          itemObj.sQuantity = '0';
        } else {
          itemObj.sQuantity = '1';
        }

        itemObj.sRelationshipType = relationshipTypes[element.type];
        itemObj.sSalesTax = '0.00';
        itemObj.sPaymentReceiver = '1';

        utagModel[`${this.prefix}oaItems`].push(itemObj);
        nativeWin.oCMOrder.oaItems.push(itemObj);
      });
    }

    // Set payment

    nativeWin.oCMOrder.sOrderAmount = model.dueNow;
    utagModel[`${this.prefix}sOrderAmount`] = model.dueNow;

    nativeWin.oCMOrder.oDiscount.sAmount = model.discount;
    utagModel[`${this.prefix}oDiscount.sAmount`] = model.discount;

  }

  public SetDiscountCode(code: string): void {
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;
    utagModel[`${this.prefix}oDiscount.sCode`] = code;
    nativeWin.oCMOrder.oDiscount.sCode = code;
  }

  public setUserOrderID(userOrderId: number): void {
    // TODO: Make DRY, this is virtually identical to the method SetDiscountCode
    // however in the interest of time and not refactoring/potentially breaking
    // existing functionality, I am doing this hacky thing instead.
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;

    // for now let's force iProcess and iQuestionnaire IDs
    utagModel[`${this.prefix}iProcess`] = '2';
    utagModel[`ocmorder_iprocess`] = '2';
    utagModel[`${this.prefix}iQuestionnaire`] = '27';

    // how many different places can we set orderId? Let's set it in all of them
    utagModel[`${this.prefix}sUserOrderID`] = userOrderId.toString();
    utagModel['js_page.lzt_orderid'] = userOrderId.toString();
    nativeWin.oCMOrder.sUserOrderID = userOrderId.toString();
    nativeWin.lzt_orderid = userOrderId.toString();

    // also set customerID
    utagModel[`${this.prefix}oUser.sCustomerID`] = this.appService.app.customerId.toString();
    nativeWin.oCMOrder.oUser.sCustomerID = this.appService.app.customerId.toString();
  }

  public SetECommerceTracking(customerInfo: any, orderInfo: any): void {
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;
    const model = customerInfo;
    const userDetails = model.primaryContactInfo;
    const orderModel = orderInfo;

    utagModel[`${this.prefix}oUser.sCustomerState`] = userDetails.StateName != null ? userDetails.StateName : '';
    utagModel[`${this.prefix}oUser.sCustomerZip`] = userDetails.Zip != null ? userDetails.Zip : '';
    utagModel[`${this.prefix}oUser.sEmail`] = userDetails.Email1 != null ? userDetails.Email1 : '';
    utagModel[`${this.prefix}oUser.sFName`] = userDetails.FirstName != null ? userDetails.FirstName : '';
    utagModel[`${this.prefix}oUser.sLName`] = userDetails.LastName != null ? userDetails.LastName : '';
    if (model.isThreePay) {
      nativeWin.oCMOrder.sPaymentPlan = 'Three Pay';
      utagModel[`${this.prefix}sPaymentPlan`] = 'Three Pay';
    } else {
      nativeWin.oCMOrder.sPaymentPlan = 'Single Pay';
      utagModel[`${this.prefix}sPaymentPlan`] = 'Single Pay';
    }

    utagModel[`${this.prefix}sUserOrderID`] = orderModel.OrderId;
    nativeWin.oCMOrder.sUserOrderID = orderModel.OrderId;
    nativeWin.lzt_orderid = orderModel.OrderId;
    utagModel['js_page.lzt_orderid'] = orderModel.OrderId;

    nativeWin.ga_store_flag = 'lz_llc_optimization_experiment';
    utagModel['js_page.ga_store_flag'] = 'lz_llc_optimization_experiment';
  }

  // not set until order has been processed and confirmed
  public SetGA_Arrays(pageType: string): void {
    this.trackingModel = Object.assign(this.trackingModel, {
      // GA arrays
      'js_page.ga_arrays.sAmount': [],
      'js_page.ga_arrays.sProductID': [],
      'js_page.ga_arrays.sQuantity': [],
      'js_page.ga_arrays.gaCat': [],
      'js_page.ga_arrays.sku': [],
      'js_page.ga_arrays.sRelationshipType': [],
      'js_page.ga_arrays.sSalesTax': [],
      'js_page.ga_arrays.sProductName': [],
    });
    const nativeWin = this.winRef.nativeWindow;
    const utagModel = this.trackingModel;

    const techAlphaOrder = (this.cookieService.get('tech_alpha') || 'notset');
    const gaOrderProcessed = (this.cookieService.get('ga_order_id') || 'notset');
    // tslint:disable-next-line: variable-name
    let tea_state_selected = (document.cookie.match('(^|;) ?lz-optimizely-exp=([^;]*)(;|$)')) ? (document.cookie.match('(^|;) ?lz-optimizely-exp=([^;]*)(;|$)'))[2] : '';
    tea_state_selected = tea_state_selected.substring(0, tea_state_selected.indexOf('|'));

    if (!nativeWin.oCMOrder.sUserOrderID.toString().match(/^2/) && (gaOrderProcessed === 'notset' || gaOrderProcessed !== nativeWin.oCMOrder.sUserOrderID)) {
      nativeWin.ga_arrays = {};
      nativeWin.ga_sub_total = 0;
      nativeWin.ga_parent_total = 0;
      let tempCat;
      let tempEvent;
      utagModel['js_page.lzt_orderid'] = nativeWin.oCMOrder.sUserOrderID;
      utagModel['js_page.lzt_ordertotal'] = nativeWin.oCMOrder.sOrderAmount;
      nativeWin.lzt_ordertotal = nativeWin.oCMOrder.sOrderAmount;
      utagModel['js_page.lzt_promocode'] = nativeWin.oCMOrder.oDiscount.sCode;
      nativeWin.lzt_promocode = nativeWin.oCMOrder.oDiscount.sCode;
      utagModel['js_page.lzt_customerid'] = nativeWin.oCMOrder.oUser.sCustomerID;
      nativeWin.lzt_customerid = nativeWin.oCMOrder.oUser.sCustomerID;
      utagModel['js_page.lzt_discountamount'] = nativeWin.oCMOrder.oDiscount.sAmount;
      nativeWin.lzt_discountamount = nativeWin.oCMOrder.oDiscount.sAmount;
      utagModel['js_page.lzt_purchasetype'] = '';
      nativeWin.lzt_purchasetype = '';

      const gaCategories = {
        'expedited 501(c)3 application': '501c3 preparation',
        'standard 501(c)3 application': '501c3 preparation',
        'standard amendment package': 'amendment',
        'expedited amendment package': 'amendment',
        'annual reports standard package': 'annual reports',
        'annual reports rush package': 'annual reports',
        'prenuptial agreement': 'attorney assisted prenuptial agreement',
        'business advantage pro monthly membership': 'business advantage pro standalone',
        'business advantage pro annual membership': 'business advantage pro standalone',
        'business advantage pro 6 month membership': 'business advantage pro standalone',
        'business advantage pro 7 day trial': 'business advantage pro trial',
        '2-day trial - business advantage pro': 'business advantage pro trial',
        'bylaws & resolutions standard package': 'bylaws and resolutions',
        'bylaws & resolutions rush package': 'bylaws and resolutions',
        'standard certificate of good standing': 'certificate of good standing',
        'rush certificate of good standing': 'certificate of good standing',
        'certified copies standard package': 'certified copies',
        'certified copies rush package': 'certified copies',
        'compliance calendar': 'compliance calendar',
        'standard conversion': 'conversion',
        'expedited conversion': 'conversion',
        'basic copyright': 'copyright',
        'comprehensive copyright': 'copyright',
        'standard company dissolution': 'corporate dissolution',
        'summary dissolution': 'corporate dissolution',
        'express company dissolution': 'corporate dissolution',
        'corporate minutes annual subscription': 'corporate minutes annual subscription',
        'standard dba': 'dba',
        'premium dba': 'dba',
        'standard design patent package': 'design patent',
        'gold design patent package': 'design patent',
        'standard divorce': 'divorce',
        'ein obtainment': 'ein obtainment',
        'entity name check': 'entity name availability check',
        'entity name reservation': 'entity name reservation',
        'standard foreign qualification': 'foreign qualification',
        'expedited foreign qualification': 'foreign qualification',
        'legal advantage': 'forms standalone',
        'general partnership agreement': 'general partnership agreement',
        'express gold incorporation': 'inc',
        'economy incorporation': 'inc',
        'standard incorporation': 'inc',
        'rush initial reports package': 'initial reports',
        'standard initial reports package': 'initial reports',
        'comprehensive last will and testament': 'last will and testament',
        'basic last will and testament': 'last will and testament',
        'basic last will': 'last will and testament',
        'legal advantage plus monthly membership': 'legal advantage plus standalone',
        'legal advantage plus 6 month membership': 'legal advantage plus standalone',
        'legal advantage plus 3 month membership': 'legal advantage plus standalone',
        'legalzoom employee advantage plan': 'legal advantage plus standalone',
        'legal forms': 'legal forms',
        'comprehensive living trust': 'living trust',
        'basic living trust': 'living trust',
        'comprehensive living will': 'living will',
        'basic living will': 'living will',
        'economy llc': 'llc',
        'deposit for llc': 'llc',
        'express gold llc': 'llc',
        'standard llc': 'llc',
        'llc - get me started': 'llc',
        'llc - support me': 'llc',
        'llc - help me grow': 'llc',
        'get me started': 'llc',
        'support me': 'llc',
        'help me grow': 'llc',
        'economy limited liability partnership': 'llp',
        'standard limited liability partnership': 'llp',
        'express gold limited liability partnership': 'llp',
        'standard limited partnership': 'lp',
        'express gold limited partnership': 'lp',
        'economy limited partnership': 'lp',
        'adult name change': 'name change',
        'minor name change': 'name change',
        'economy non-profit corporation': 'nonprofit',
        'express platinum non-profit corporation': 'nonprofit',
        'express gold non-profit corporation': 'nonprofit',
        'standard non-profit corporation': 'nonprofit',
        'standard operating agreement': 'operating agreements',
        'rush operating agreement': 'operating agreements',
        'patent search': 'patent search',
        'gold pet protection agreement &reg;': 'pet protection agreement',
        'standard pet protection agreement &reg;': 'pet protection agreement',
        'premium pet protection agreement &reg;': 'pet protection agreement',
        'comprehensive power of attorney': 'power of attorney',
        'basic power of attorney': 'power of attorney',
        'gold power of attorney': 'power of attorney',
        'professional patent drawings': 'professional patent drawings',
        'provisional application for patent': 'provisional patent',
        'registered agent fee (1 year)': 'ra-standalone',
        'standard real estate deed transfer': 'real estate deed transfer',
        'express gold real estate deed transfer': 'real estate deed transfer',
        'deluxe lease package': 'real estate lease',
        'free edition lease package': 'real estate lease',
        'premier lease package': 'real estate lease',
        'sellers permit standard': 'sellers permit',
        'rush state tax id': 'state tax id',
        'standard state tax id': 'state tax id',
        'basic trademark': 'trademark',
        'complete coverage trademark': 'trademark',
        'trademark standard package': 'trademark',
        'trademark statement of use': 'trademark statement of use',
        'trademark plus package': 'trademark',
        'trademark gold package': 'trademark',
        'trademark assignment recordation': 'trademark assignment',
        'trademark monitoring': 'trademark monitoring',
        'comprehensive federal, state, and common law search': 'trademark search',
        'comprehensive federal and state search': 'trademark search',
        'worldscan comprehensive international search': 'trademark search',
        'trademark section 8 declaration of use': 'trademark section 8 declaration',
        'trademark renewal': 'trademark section 9 renewal',
        'trademark statement of use extension': 'trademark statement of use extension',
        'utility patent: step 1 - plager schack llp': 'utility patent',
        'utility patent: step 1 - canyon law group': 'utility patent',
        'patent filing package - canyon law group': 'utility patent step 2',
        'patent filing package - plager schack llp': 'utility patent step 2',
        'enhanced patent filing package - plager schack llp': 'utility patent step 2',
        'enhanced patent filing package - canyon law group': 'utility patent step 2',
        'patent filing package - dunlap bennett & ludwig pllc': 'utility patent step 2',
        'utility patent: step 1 - dunlap bennett & ludwig pllc': 'utility patent',
        'enhanced patent filing package - dunlap bennett & ludwig pllc': 'utility patent step 2',
        'last will revision': 'last will revision',
        'living trust revision': 'living trust revision',
        'living will alert card': 'living will',
        'living will revision': 'living will revision',
        'poa revision': 'poa revision',
        'real estate lease revisions': 'real estate lease revision',
        'registered agent services': 'ra-standalone',
        'registered agent service': 'ra-standalone',
        'standard living will': 'living will',
        'managed compliance package with registered agent': 'managed compliance package with ra',
        'managed compliance package': 'managed compliance package',
        'compliance package with account manager': 'managed compliance package',
        'standard design patent package - canyon law group': 'design patent',
        'gold design patent package - canyon law group': 'design patent',
        'pet protection agreement &reg; revision': 'pet protection agreement revision',
        'business advantage pro attorney consultation': 'attorney consultation requests',
        'legal advantage plus attorney consultation': 'attorney consulation requests',
        'ra renewal - 1 year': 'ra renewal',
        'prepared corporate minutes': 'corporate minutes',
        '3-month compliance package with 30-day trial': 'compliance standalone',
        'state tax id': 'state tax id',
        'llc gold': 'llc',
        '5 day trial - business advantage pro': 'business advantage pro trial',
        'company minutes annual subscription': 'corporate minutes annual subscription',
        'legal advantage plus annual membership': 'legal advantage plus standalone',
        'llc silver': 'llc',
        'utility patent: step 1 - dunlapweaver pllc': 'utility patent',
        'llc bronze': 'llc',
        'patent filing package - dunlapweaver pllc': 'utility patent step 2',
        'gold design patent package - dunlapweaver pllc': 'design patent',
        'managed compliance package with 30-day trial': 'compliance standalone',
        'standard design patent package - dunlapweaver pllc': 'design patent',
        'enhanced patent filing package - dunlapweaver pllc': 'utility patent step 2',
        'sams club family bundle': 'sams club family bundle',
        'basic llc': 'llc',
        'priority llc': 'llc',
        'priority plus llc': 'llc',
        'estate plan bundle': 'estate plan bundle',
        'estate plan bundle last will and testament - for one': 'estate plan bundle',
        'estate plan bundle last will and testament - for two': 'estate plan bundle',
        'estate plan bundle living trust - for one': 'estate plan bundle',
        'estate plan bundle living trust - for two': 'estate plan bundle',
        'attorney-led trademark registration': 'attorney-led trademark registration',
        'llc formation essentials': 'llc',
        'llc complete coverage': 'llc',
        'llc essentials plus': 'llc',
        'gold design patent package - dunlap bennett & ludwig pllc': 'design patent',
        'standard design patent package - dunlap bennett & ludwig pllc': 'design patent',
        'business advantage pro 14-day trial': 'business advantage pro trial'
      };

      // add more below as needed following same keys as gaCategories
      // get value from Analytics team
      const gaProductEvents = {
        'economy llc': 'lz_llc_optimization_experiment',
        'deposit for llc': 'lz_llc_optimization_experiment',
        'express gold llc': 'lz_llc_optimization_experiment',
        'standard llc': 'lz_llc_optimization_experiment',
      };

      nativeWin.oCMOrder.oaItems.forEach((element) => {

        // tslint:disable-next-line: no-string-literal
        switch (element['sProductName']) {
          case 'Additional Class Filing Fee':
          case 'Standard Government Filing Fee*':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '325.00';
            break;

          case 'Additional Class Processing':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '60.00';
            break;

          case 'Additional Classes Preparation':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '25.00';
            break;

          case 'Logo Search Additional Class':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '100.00';
            break;

          case 'Section 15 Form Preparation':
          case 'Last Will and Testament':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '49.00';
            break;

          case 'Trademark Search':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '99.00';
            break;

          case 'Federal Filing Fee - Section 15 Form':
            // tslint:disable-next-line: no-string-literal
            element['sAmount'] = '200.00';
            break;

          default:
            break;
        }

        // tslint:disable-next-line: forin
        for (const key in element) {
          nativeWin.ga_arrays[key] = (nativeWin.ga_arrays[key] || []).concat(element[key]);
          utagModel[`js_page.ga_arrays.${key}`] = (utagModel[`js_page.ga_arrays.${key}`] || []).concat(element[key]);
        }

        // hard coding product id coming as productConfigurationID;
        let skuPackageNumber;
        // tslint:disable-next-line: no-string-literal
        switch (element['sProductID']) {
          case ProductConfigurationIds.expressGoldLLCPackage:
            skuPackageNumber = ProductComponentId.expressGoldLLCPackage;
            break;
          case ProductConfigurationIds.economyLLCPackage:
            skuPackageNumber = ProductComponentId.economyLLCPackage;
            break;
          case ProductConfigurationIds.standarLLCPackage:
            skuPackageNumber = ProductComponentId.standardLLCPackage;
            break;
          case ProductConfigurationIds.tier1LLCPackage:
            skuPackageNumber = ProductComponentId.tier1LLCPackage;
            break;
          case ProductConfigurationIds.tier2LLCPackage:
            skuPackageNumber = ProductComponentId.tier2LLCPackage;
            break;
          case ProductConfigurationIds.tier3LLCPackage:
            skuPackageNumber = ProductComponentId.tier3LLCPackag;
            break;
          case 2400:
            skuPackageNumber = 1401;
            break;
          // tslint:disable-next-line: no-string-literal
          default: skuPackageNumber = element['sProductID'];
        }

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sku'] = (nativeWin.ga_arrays['sku'] || []).concat(skuPackageNumber + ' - ' + element['sRelationshipType']);
        // tslint:disable-next-line: no-string-literal
        utagModel['js_page.ga_arrays.sku'] = (utagModel['js_page.ga_arrays.sku'] || []).concat(`${skuPackageNumber} - ${element['sRelationshipType']}`);

        // tslint:disable-next-line: no-string-literal
        if ((element['sRelationshipType'] !== 'Shipping') && (element['sRelationshipType'] !== 'FilingFee') && (element['sRelationshipType'] !== 'UnSet')) {

          // tslint:disable-next-line: no-string-literal
          nativeWin.ga_sub_total += parseFloat(element['sAmount']);
          // tslint:disable-next-line: no-string-literal
          utagModel['js_page.ga_sub_total'] += parseFloat(element['sAmount']);

          // tslint:disable-next-line: no-string-literal
          if (element['sRelationshipType'] === 'Package' || element['sRelationshipType'] === 'Revision') {

            // tslint:disable-next-line: no-string-literal
            nativeWin.ga_parent_total = parseFloat(element['sAmount']);
            // tslint:disable-next-line: no-string-literal
            utagModel['js_page.ga_parent_total'] = parseFloat(element['sAmount']);
            // tslint:disable-next-line: no-string-literal
            tempCat = gaCategories[element['sProductName'].toLowerCase()];
            // tslint:disable-next-line: no-string-literal
            tempEvent = gaProductEvents[element['sProductName'].toLowerCase()];
            // tslint:disable-next-line: no-string-literal
            nativeWin.lzt_purchasetype = element['sRelationshipType'];
            // tslint:disable-next-line: no-string-literal
            utagModel['js_page.lzt_purchasetype'] = element['sRelationshipType'];

            // tslint:disable-next-line: no-string-literal
          } else if ((!tempCat) && (element['sRelationshipType'] === 'AddOn' || element['sRelationshipType'] === 'CrossSell') && (element['sAmount'] > 0)) {

            // tslint:disable-next-line: no-string-literal
            nativeWin.ga_parent_total = parseFloat(element['sAmount']);
            // tslint:disable-next-line: no-string-literal
            utagModel['js_page.ga_parent_total'] = parseFloat(element['sAmount']);
            // tslint:disable-next-line: no-string-literal
            tempCat = gaCategories[element['sProductName'].toLowerCase()];

            // tslint:disable-next-line: no-string-literal
          } else if ((!tempCat) && (element['sRelationshipType'] === 'SubscriptionUsage')) {
            // tslint:disable-next-line: no-string-literal
            tempCat = gaCategories[element['sProductName'].toLowerCase()];
          }
        }
      });

      // pass through of discount info to order info
      if ((typeof nativeWin.oCMOrder.oDiscount.sAmount !== 'undefined') && (nativeWin.oCMOrder.oDiscount.sAmount !== 0)) {

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sAmount'] = (nativeWin.ga_arrays['sAmount'] || []).concat('-' + nativeWin.oCMOrder.oDiscount.sAmount);
        utagModel['js_page.ga_arrays.sAmount'] = (utagModel['js_page.ga_arrays.sAmount'] || []).concat('-' + nativeWin.oCMOrder.oDiscount.sAmount);

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sProductID'] = (nativeWin.ga_arrays['sProductID'] || []).concat(nativeWin.oCMOrder.oDiscount.sCode || 'unknown');
        utagModel['js_page.ga_arrays.sProductID'] = (utagModel['js_page.ga_arrays.sProductID'] || []).concat(nativeWin.oCMOrder.oDiscount.sCode || 'unknown');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sProductName'] = (nativeWin.ga_arrays['sProductName'] || []).concat('Discount');
        utagModel['js_page.ga_arrays.sProductName'] = (utagModel['js_page.ga_arrays.sProductName'] || []).concat('Discount');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sQuantity'] = (nativeWin.ga_arrays['sQuantity'] || []).concat('1');
        utagModel['js_page.ga_arrays.sQuantity'] = (utagModel['js_page.ga_arrays.sQuantity'] || []).concat('1');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sRelationshipType'] = (nativeWin.ga_arrays['sRelationshipType'] || []).concat('Discount');
        utagModel['js_page.ga_arrays.sRelationshipType'] = (utagModel['js_page.ga_arrays.sRelationshipType'] || []).concat('Discount');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sSalesTax'] = (nativeWin.ga_arrays['sSalesTax'] || []).concat('0.00');
        utagModel['js_page.ga_arrays.sSalesTax'] = (utagModel['js_page.ga_arrays.sSalesTax'] || []).concat('0.00');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sku'] = (nativeWin.ga_arrays['sku'] || [])
          .concat(((typeof nativeWin.oCMOrder.oDiscount.sCode !== 'undefined'
            && nativeWin.oCMOrder.oDiscount.sCode !== '') ? nativeWin.oCMOrder.oDiscount.sCode : (typeof nativeWin.oCMOrder.partner !== 'undefined' ? nativeWin.oCMOrder.partner : 'unknown')) + ' - Discount');

        utagModel['js_page.ga_arrays.sku'] = (utagModel['js_page.ga_arrays.sku'] || [])
          .concat(((typeof nativeWin.oCMOrder.oDiscount.sCode !== 'undefined'
            && nativeWin.oCMOrder.oDiscount.sCode !== '') ? nativeWin.oCMOrder.oDiscount.sCode : (typeof nativeWin.oCMOrder.partner !== 'undefined' ? nativeWin.oCMOrder.partner : 'unknown')) + ' - Discount');

        // update order total to include discount
        nativeWin.lzt_ordertotal = (parseFloat(nativeWin.oCMOrder.sOrderAmount) - parseFloat(nativeWin.oCMOrder.oDiscount.sAmount)).toFixed(2);
        utagModel['js_page.lzt_ordertotal'] = nativeWin.lzt_ordertotal;
        nativeWin.ga_sub_total = Math.round((parseFloat(nativeWin.ga_sub_total) - parseFloat(nativeWin.oCMOrder.oDiscount.sAmount)) * 100) / 100;
        utagModel['js_page.ga_sub_total'] = nativeWin.ga_sub_total;
      }

      // pass through of selected state to order info
      if (typeof tea_state_selected !== 'undefined' && tea_state_selected.length > 0) {
        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sAmount'] = (nativeWin.ga_arrays['sAmount'] || []).concat('0');
        utagModel['js_page.ga_arrays.sAmount'] = (utagModel['js_page.ga_arrays.sAmount'] || []).concat('0');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sProductID'] = (nativeWin.ga_arrays['sProductID'] || []).concat('state');
        utagModel['js_page.ga_arrays.sProductID'] = (utagModel['js_page.ga_arrays.sProductID'] || []).concat('state');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sProductName'] = (nativeWin.ga_arrays['sProductName'] || []).concat(tea_state_selected);
        utagModel['js_page.ga_arrays.sProductName'] = (utagModel['js_page.ga_arrays.sProductName'] || []).concat('tea_state_selected');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sQuantity'] = (nativeWin.ga_arrays['sQuantity'] || []).concat('1');
        utagModel['js_page.ga_arrays.sQuantity'] = (utagModel['js_page.ga_arrays.sQuantity'] || []).concat('1');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sRelationshipType'] = (nativeWin.ga_arrays['sRelationshipType'] || []).concat('Selected_State');
        utagModel['js_page.ga_arrays.sRelationshipType'] = (utagModel['js_page.ga_arrays.sRelationshipType'] || []).concat('Selected_State');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sSalesTax'] = (nativeWin.ga_arrays['sSalesTax'] || []).concat('0.00');
        utagModel['js_page.ga_arrays.sSalesTax'] = (utagModel['js_page.ga_arrays.sSalesTax'] || []).concat('0.00');

        // tslint:disable-next-line: no-string-literal
        nativeWin.ga_arrays['sku'] = (nativeWin.ga_arrays['sku'] || []).concat('state' + ' - ' + tea_state_selected);
        utagModel['js_page.ga_arrays.sku'] = (utagModel['js_page.ga_arrays.sku'] || []).concat(`state - ${tea_state_selected}`);
      }

      // tslint:disable-next-line: no-string-literal
      if (typeof tempCat !== 'undefined' && nativeWin.ga_arrays['sProductID']) {
        // tslint:disable-next-line: no-string-literal prefer-for-of
        for (let i = 0; i < nativeWin.ga_arrays['sProductID'].length; i++) {
          // tslint:disable-next-line: no-string-literal
          nativeWin.ga_arrays['gaCat'] = (nativeWin.ga_arrays['gaCat'] || []).concat(tempCat);
          utagModel['js_page.ga_arrays.gaCat'] = (utagModel['js_page.ga_arrays.gaCat'] || []).concat(tempCat);
        }
      }

      if (typeof tempEvent !== 'undefined') {
        // tslint:disable-next-line: no-string-literal
        utagModel['ga_event_category'] = tempEvent;
        // tslint:disable-next-line: no-string-literal
        utagModel['ga_event_action'] = 'load';
        // tslint:disable-next-line: no-string-literal
        utagModel['ga_event_label'] = 'Transaction ID';
        // tslint:disable-next-line: no-string-literal
        utagModel['ga_event_value'] = nativeWin.lzt_orderid;
      }

      utagModel['js_page.oCMOrder.submitToGA'] = true;
      this.sendAdConversion(pageType, nativeWin.ga_sub_total, nativeWin.lzt_orderid.toString());
      this.TriggerTracking(pageType);
    }
  }

  public sendAdConversion(pageType: string = '', revenue: number = 0, orderID: number = 0): void {
    const nativeWin = this.winRef.nativeWindow;
    // check if ga ads script is available
    if (nativeWin.LZWebsite !== undefined && nativeWin.LZWebsite.GaAds) {
      switch (pageType) {
        case PagePath.BusinessState: {
          nativeWin.LZWebsite.GaAds.tasks.handleQStart();
          break;
        }
        case PagePath.ReviewYourOrder: {
          nativeWin.LZWebsite.GaAds.tasks.handleCart();
          break;
        }
        case PagePath.Checkout: {
          nativeWin.LZWebsite.GaAds.tasks.handleCheckout(revenue);
          break;
        }
        case PagePath.ExpressOffersPage: {
          nativeWin.LZWebsite.GaAds.tasks.handleConfirmation(revenue, orderID);
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  triggerCheckoutPixels(name: string, price: number): void {
    this.setPixels(PagePath.Checkout, name, price);
  }

  private setPixels(pagePath: PagePath, name: string, price: number): void {
    this.sendAdConversion(pagePath, price);
    this.setCheckoutTrackingVariables({ revenue: price, pkgName: `LLC - ${name}` });
    if (pagePath === PagePath.Checkout) {
      this.TriggerTracking('checkoutPage');
    }
  }

  private loadScript(src: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // load script
      const script: any = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = src;

      // IE
      if (script.readyState) {
        script.onreadystatechange = () => {
          if (script.readyState === 'loaded' || script.readyState === 'complete') {
            script.onreadystatechange = null;
            resolve({ loaded: true, status: 'Loaded', src });
          }
        };
      } else {
        script.onload = () => {
          resolve({ loaded: true, status: 'Loaded', src });
        };
      }

      script.onerror = (error: any) => resolve({ loaded: false, status: 'Loaded', src });
      document.getElementsByTagName('head')[0].appendChild(script);
    }
    );
  }

  private loadExternalTracker(varExistenceCheck: any, url: string, callback: () => void): void {
    if (typeof varExistenceCheck !== 'undefined') { callback(); }
    this.loadScript(url).then(callback);
  }

  public triggerClickTrack(eventCategory: string, eventLabel: string, eventAction: string = 'click'): void {
    const nativeWin = this.winRef.nativeWindow;
    if (nativeWin.utag !== undefined) {
      nativeWin.utag.link({
        ga_event_action: eventAction,
        ga_event_category: eventCategory,
        ga_event_label: eventLabel
      }, () => {
        if (nativeWin.utag_data) {
          nativeWin.utag_data.ga_event_action = '';
          nativeWin.utag_data.ga_event_category = '';
          nativeWin.utag_data.ga_event_label = '';
        }
      });
    }
  }
}
